/**
 * NotificationsTab — Page dédiée "Notifications" (Admin/Manager).
 *
 * Consolide toutes les notifications en attente en une seule vue cliquable :
 *   - Besoins, Bons de commande, Achats, Annulations, Modifications,
 *     Factures à valider, Points financiers, Pourboires, Notes.
 * Chaque ligne est cliquable → navigation vers l'onglet concerné.
 *
 * Alimenté par le hook useNotifications (effectiveCounts + notifLatest).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, ChevronRight, Sparkles } from "lucide-react";
import {
  COUNT_META,
  COLOR_BG,
  formatRelativeTime,
} from "../utils/notifications";

const NotificationsTab = ({
  effectiveCounts = {},
  effectiveTotal = 0,
  notifLatest = {},
  onOpenNotif,
  onMarkAllRead,
}) => {
  const entries = Object.entries(effectiveCounts)
    .filter(([, v]) => (Number(v) || 0) > 0)
    .sort((a, b) => (b[1] || 0) - (a[1] || 0));

  return (
    <div className="space-y-4" data-testid="notifications-tab">
      {/* HERO HEADER */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-900/40 via-slate-900/40 to-rose-900/30 border border-amber-500/20 p-4 sm:p-5">
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
              <Bell className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.4} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-black text-white leading-tight tracking-tight">Notifications</h2>
              <p className="text-[13px] text-slate-300 leading-tight mt-0.5">
                {effectiveTotal > 0 ? (
                  <>
                    <span className="text-amber-300 font-bold">{effectiveTotal}</span>
                    <span className="text-slate-400"> élément{effectiveTotal > 1 ? "s" : ""} en attente</span>
                  </>
                ) : (
                  <span className="text-slate-400">Tout est à jour</span>
                )}
              </p>
            </div>
          </div>
          {effectiveTotal > 0 && (
            <Button
              onClick={onMarkAllRead}
              className="bg-slate-800/70 border border-slate-700 hover:bg-slate-700 text-slate-200 h-10"
              data-testid="notifications-tab-mark-all"
            >
              <CheckCircle className="w-4 h-4 mr-1.5 text-emerald-400" strokeWidth={2.4} />
              Tout marquer lu
            </Button>
          )}
        </div>
      </div>

      {/* LISTE */}
      {entries.length === 0 ? (
        <div className="rounded-2xl border border-slate-700/60 bg-slate-800/40 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-emerald-400" strokeWidth={2.2} />
          </div>
          <p className="text-white text-lg font-bold">Boîte de réception vide</p>
          <p className="text-slate-400 text-sm mt-1">Aucune notification en attente pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {entries.map(([key, count]) => {
            const meta = COUNT_META[key] || { color: "red", label: key };
            const bg = COLOR_BG[meta.color] || COLOR_BG.slate;
            const rel = formatRelativeTime(notifLatest?.[key]);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onOpenNotif(key)}
                className="group flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700/70 hover:border-slate-600 hover:bg-slate-800 transition-all text-left"
                data-testid={`notifications-tab-item-${key}`}
              >
                <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center flex-shrink-0 shadow-md`}>
                  <span className="text-white font-black text-[15px] tabular-nums">
                    {count > 99 ? "99+" : count}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-[14px] leading-tight truncate">{meta.label}</p>
                  {rel && <p className="text-[11.5px] text-slate-500 mt-0.5">{rel}</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
